import React, { useState } from 'react';
import { Card, CardBody, Row, Col, Input, Badge, Button, ButtonGroup } from 'reactstrap';
import { User, Mail, Activity, Video, Wind } from 'lucide-react';

const PatientsList = ({ subjects, onPatientSelect, selectedSubject }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredPatient, setHoveredPatient] = useState(null);

  const filteredPatients = subjects.filter(patient =>
    patient.subjectName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePatientAction = (patient, action) => {
    if (patient.studyCompleted) return;
    onPatientSelect(patient, action);
  };

  return (
    <div>

      <style>{`
        .action-container {
          container-type: inline-size;
        }

        @container (max-width: 360px) {
          .btn-label {
            display: none !important;
          }
          .btn-icon {
            margin-right: 0 !important;
          }
        }
      `}</style>

      <div className="mb-4">
        <div className="search-container position-relative">
          <Input
            type="text"
            placeholder="Search patients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control-lg shadow-sm"
            style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              paddingLeft: '1.5rem'
            }}
          />
        </div>
      </div>

      <div className="patients-grid">
        {filteredPatients.map(patient => (
          <Card
            key={patient.subjectId}
            className="mb-3 patient-card"
            onMouseEnter={() => setHoveredPatient(patient.subjectId)}
            onMouseLeave={() => setHoveredPatient(null)}
            style={{
              backgroundColor: patient.studyCompleted ? '#d4cfd1' : '#fff',
              borderLeft: patient.studyCompleted ? '4px solid #eb4034' : '4px solid #0d14e0',
              transition: 'all 0.2s ease',
              transform: hoveredPatient === patient.subjectId && !patient.studyCompleted ? 'translateY(-2px)' : 'none',
              boxShadow: hoveredPatient === patient.subjectId && !patient.studyCompleted 
                ? '0 4px 12px rgba(0,0,0,0.1)' 
                : '0 1px 3px rgba(0,0,0,0.05)'
            }}
          >
            <CardBody>
              <Row className="align-items-center g-0">

                {/* Left – Patient Info */}
                <Col xs={12} md={6}>
                  <div className="d-flex align-items-center mb-3 mb-md-0">
                    <div className="flex-shrink-0 me-3">
                      <div style={{
                        width: '45px',
                        height: '45px',
                        backgroundColor:
                          selectedSubject?.subjectId === patient.subjectId ? '#556ee6' : '#eef2ff',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <User
                          size={24}
                          color={selectedSubject?.subjectId === patient.subjectId ? '#fff' : '#556ee6'}
                        />
                      </div>
                    </div>

                    <div className="flex-grow-1 min-width-0">
                      <h5 className="mb-1 text-truncate">{patient.subjectName}</h5>
                      <div className="d-flex align-items-center text-muted">
                        <Mail size={14} className="me-1" />
                        <small className="text-truncate">{patient.email}</small>
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Middle – Stats */}
                <Col xs={6} md={3} className="text-center mb-2 mb-md-0">
                  <div className="d-flex flex-column align-items-center">
                    <Badge color="primary" className="mb-2 px-2 py-1">
                      {patient.todayRecordings} today
                    </Badge>
                    <small className="text-muted">
                      Total: {patient.allTimeRecordings}
                    </small>
                  </div>
                </Col>

                {/* Right – Buttons (Container Query Applied) */}
                <Col xs={6} md={3} className="text-end action-container">
                  {!patient.studyCompleted && (
                    <ButtonGroup size="sm" className="w-100">
                      <PatientActionButton
                        color="primary"
                        icon={Video}
                        label="Dashboard"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePatientAction(patient, 'dashboard');
                        }}
                        isFirst={true}
                      />

                      <PatientActionButton
                        color="success"
                        icon={Activity}
                        label="PPG Data"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePatientAction(patient, 'ppg');
                        }}
                      />

                      <PatientActionButton
                        color="warning"
                        icon={Wind}
                        label="Breathing"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePatientAction(patient, 'breathing');
                        }}
                        isLast={true}
                      />
                    </ButtonGroup>
                  )}

                  {patient.studyCompleted && (
                    <Badge color="danger" className="px-3 py-2">
                      Study Completed
                    </Badge>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        ))}

        {filteredPatients.length === 0 && (
          <div className="text-center py-4 text-muted">
            <p className="mb-0">No patients found matching "{searchTerm}"</p>
          </div>
        )}
      </div>
    </div>
  );
};


const PatientActionButton = ({ color, icon: Icon, label, onClick, isFirst, isLast }) => (
  <Button
    color={color}
    outline
    className="d-flex align-items-center justify-content-center"
    onClick={onClick}
    style={{
      borderRadius: isLast ? '0 8px 8px 0' : isFirst ? '8px 0 0 8px' : '0',
      fontSize: '0.875rem'
    }}
  >
    <Icon size={16} className="me-1 btn-icon" />
    <span className="btn-label">{label}</span>
  </Button>
);

export default PatientsList;
